// Helpers for instrumenting the upload-to-results review pipeline
// (roadmap item 21). Pure client-side utilities: no analytics transport here,
// callers pass the results to trackEvent themselves.
import { SubmitCvReviewError } from "@/lib/cv/submit-cv-review";

export type UploadErrorClass = "validation" | "network" | "server";

// Classify an upload/review submission error so cv_upload_failed and
// review_failed can distinguish user-fixable input problems from
// infrastructure failures.
export function classifyUploadError(error: unknown): UploadErrorClass {
  if (error instanceof SubmitCvReviewError) {
    if (typeof error.status === "number") {
      if (error.status >= 500) {
        return "server";
      }
      if (error.status === 401 || error.status === 403) {
        // Session problems are infrastructure-ish: nothing about the CV
        // itself was rejected.
        return "server";
      }
      return "validation";
    }

    // A 2xx response with a malformed payload: the server misbehaved.
    return "server";
  }

  // fetch() rejects with a TypeError when the network request itself fails.
  if (error instanceof TypeError) {
    return "network";
  }

  return "server";
}

const REVIEW_START_STORAGE_KEY = "hyred_review_started_at";

// Record when the review pipeline started so review_result_viewed can report
// how long the user waited. sessionStorage keeps it across the redirect to
// the review page without leaking between browser sessions.
export function markReviewStarted(now: number = Date.now()) {
  try {
    window.sessionStorage.setItem(REVIEW_START_STORAGE_KEY, String(now));
  } catch {
    // Storage unavailable (private mode, quota): skip silently.
  }
}

export function getSecondsSinceReviewStart(
  now: number = Date.now(),
): number | null {
  try {
    const raw = window.sessionStorage.getItem(REVIEW_START_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const startedAt = Number(raw);
    if (!Number.isFinite(startedAt) || startedAt <= 0 || startedAt > now) {
      return null;
    }

    return Math.round((now - startedAt) / 1000);
  } catch {
    return null;
  }
}

// Coarse score buckets so review_result_viewed stays low-cardinality.
export function getReviewScoreBand(score: number): string {
  if (!Number.isFinite(score)) {
    return "unknown";
  }
  if (score < 40) {
    return "under_40";
  }
  if (score < 60) {
    return "40_59";
  }
  if (score < 75) {
    return "60_74";
  }
  if (score < 90) {
    return "75_89";
  }
  return "90_plus";
}
